import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';

const CalendarView = ({ entries, onDeleteEntry }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDayDialog, setShowDayDialog] = useState(false);

  const days = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const getEntriesForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return entries.filter(e => e.date === dateStr);
  };

  const getTotalHoursForDay = (date) => {
    return getEntriesForDay(date).reduce((sum, e) => sum + e.hours, 0);
  };

  const changeMonth = (delta) => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  const handleDayClick = (date) => {
    setSelectedDate(date);
    setShowDayDialog(true);
  };

  const handleDelete = async (entryId) => {
    const result = await onDeleteEntry(entryId);
    if (result.success) {
      // Refresh will happen automatically
    }
  };

  const weekDays = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const startingDayIndex = (firstDayOfMonth.getDay() + 6) % 7; // Adjust for Monday start

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-6">
        <h2 className="font-display text-3xl font-bold gold-text mb-2">Calendario</h2>
        <p className="text-muted-foreground">Le tue ore, giorno per giorno.</p>
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-between mb-6">
        <Button variant="outline" size="sm" onClick={() => changeMonth(-1)} data-testid="prev-month">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h3 className="font-display text-xl font-semibold">
          {format(currentMonth, 'MMMM yyyy', { locale: it })}
        </h3>
        <Button variant="outline" size="sm" onClick={() => changeMonth(1)} data-testid="next-month">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Calendar grid */}
      <Card className="card-mafia">
        <CardContent className="p-4">
          {/* Week days header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Empty cells for days before month starts */}
            {Array.from({ length: startingDayIndex }).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square" />
            ))}

            {/* Actual days */}
            {days.map((day) => {
              const dayEntries = getEntriesForDay(day);
              const totalHours = getTotalHoursForDay(day);
              const hasEntries = dayEntries.length > 0;

              return (
                <button
                  key={day.toISOString()}
                  onClick={() => handleDayClick(day)}
                  className={`
                    aspect-square p-1 rounded-lg border transition-all
                    ${isToday(day) ? 'border-primary' : 'border-transparent'}
                    ${hasEntries ? 'bg-muted hover:bg-muted/80' : 'hover:bg-muted/30'}
                    flex flex-col items-center justify-center
                  `}
                  data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                >
                  <span className={`text-sm ${isToday(day) ? 'font-bold gold-text' : ''}`}>
                    {format(day, 'd')}
                  </span>
                  {hasEntries && (
                    <span className="text-xs gold-text font-semibold mt-1">
                      {totalHours}h
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Day detail dialog */}
      <Dialog open={showDayDialog} onOpenChange={setShowDayDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {selectedDate && format(selectedDate, 'EEEE d MMMM yyyy', { locale: it })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {selectedDate && getEntriesForDay(selectedDate).length > 0 ? (
              getEntriesForDay(selectedDate).map((entry) => (
                <div
                  key={entry.id}
                  className="p-3 bg-muted/30 rounded-lg flex items-start justify-between"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge>{entry.commessa_name}</Badge>
                      {entry.project_name && (
                        <Badge variant="secondary">{entry.project_name}</Badge>
                      )}
                      <span className="gold-text font-semibold">{entry.hours}h</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{entry.description}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(entry.id)}
                    className="text-muted-foreground hover:text-destructive"
                    data-testid={`delete-entry-${entry.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">
                Nessuna registrazione per questo giorno
              </p>
            )}
          </div>
          {selectedDate && getEntriesForDay(selectedDate).length > 0 && (
            <div className="pt-4 border-t border-border">
              <p className="text-right font-semibold">
                Totale: <span className="gold-text">{getTotalHoursForDay(selectedDate)}h</span>
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarView;
